import { LinkIcon } from '@phosphor-icons/react';
import {
  Button,
  Cell,
  Column,
  Modal,
  Row,
  SearchField,
  Spinner,
  type Selection,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
  TextField,
} from '@skupreme/shared/ui';
import { useState } from 'react';
import { useMappingItems } from '../hooks/use-mapping-items';
import { mappingErrorMessage } from '../lib/mapping-errors';
import type { ListingRef } from '../lib/types';

export type PostgresItemPickerModalProps = {
  /** Shows the dialog while set. */
  listing: Pick<ListingRef, 'title' | 'state'> | null;
  onClose: () => void;
  onSelect: (itemId: string, packQuantity: number) => void;
  isProcessing?: boolean;
  /** Adds a pack quantity field (how many units of the item the listing sells). */
  showPack?: boolean;
  /** The pack the field opens with, e.g. the suggested one. */
  defaultPack?: number;
};

const isPackQuantity = (pack: string) =>
  /^\d+$/.test(pack.trim()) && Number(pack) >= 1;

/** Picks the catalog item a listing is mapped to, from a search of the account's items. */
export function PostgresItemPickerModal({
  listing,
  onClose,
  onSelect,
  isProcessing,
  showPack,
  defaultPack = 1,
}: PostgresItemPickerModalProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [pack, setPack] = useState(String(defaultPack));
  const { items, loading, error } = useMappingItems(search, !!listing);

  /* Every listing opens on an empty search. */
  const [openedFor, setOpenedFor] = useState(listing);
  if (openedFor !== listing) {
    setOpenedFor(listing);
    setSearch('');
    setSelected(null);
    setPack(String(defaultPack));
  }

  const verb = listing?.state === 'mapped' ? 'Re-map' : 'Map';

  return (
    <Modal
      isOpen={!!listing}
      onOpenChange={isOpen => !isOpen && onClose()}
      size="md"
      showCloseButton
      isDismissable={!isProcessing}
      title={
        <span className="sk:flex sk:flex-col sk:gap-0.5">
          {verb} to catalog item
          {listing?.title ? (
            <span className="sk:truncate sk:text-dense sk:font-normal sk:text-fg-secondary">
              {listing.title}
            </span>
          ) : null}
        </span>
      }
      footer={
        <div className="sk:flex sk:justify-end sk:gap-2">
          <Button
            variant="pill"
            size="md"
            isDisabled={isProcessing}
            onPress={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            isDisabled={
              !selected || isProcessing || (showPack && !isPackQuantity(pack))
            }
            onPress={() => selected && onSelect(selected, Number(pack))}
          >
            <LinkIcon size={14} weight="bold" aria-hidden />
            {verb}
          </Button>
        </div>
      }
    >
      <div className="sk:flex sk:flex-col sk:gap-3">
        <SearchField
          aria-label="Search catalog items"
          placeholder="Search by title or SKU..."
          value={search}
          onChange={setSearch}
          isLoading={loading}
          autoFocus
          className="sk:w-full"
        />
        {showPack ? (
          <div className="sk:flex sk:items-center sk:gap-3">
            <span
              id="mapping-pack-quantity"
              className="sk:text-dense sk:text-fg-secondary"
            >
              Pack quantity (units of the product in this listing)
            </span>
            <TextField
              aria-labelledby="mapping-pack-quantity"
              type="number"
              value={pack}
              onChange={setPack}
              isInvalid={!isPackQuantity(pack)}
              className="sk:w-24"
            />
          </div>
        ) : null}
        <TableContainer className="sk:max-h-96 sk:overflow-y-auto">
          <Table
            aria-label="Catalog items"
            selectionMode="single"
            disallowEmptySelection={false}
            selectedKeys={selected ? [selected] : []}
            onSelectionChange={(keys: Selection) => {
              const [key] = keys === 'all' ? [] : [...keys];
              setSelected(key ? String(key) : null);
            }}
          >
            <TableHeader>
              <Column id="item" isRowHeader>
                Item
              </Column>
            </TableHeader>
            <TableBody
              renderEmptyState={() => (
                <div className="sk:flex sk:justify-center sk:px-3 sk:py-8 sk:text-dense sk:text-fg-tertiary">
                  {loading ? (
                    <Spinner size="sm" label="Searching" />
                  ) : error ? (
                    mappingErrorMessage(error)
                  ) : (
                    'No catalog items found'
                  )}
                </div>
              )}
            >
              {items.map(item => (
                <Row key={item.id} id={item.id} className="sk:cursor-pointer">
                  <Cell>
                    <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-0.5">
                      <span className="sk:truncate sk:text-sm sk:text-fg">
                        {item.title}
                      </span>
                      <span className="sk:text-dense sk:text-fg-secondary">
                        {[item.sku, item.brand].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                  </Cell>
                </Row>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Modal>
  );
}
